import React from "react";
import { useTableHooks } from "../../../atoms/table/useTableHooks";
import { pascalToSnakeCase } from "../../../../utils";
import { Pagination } from "../../../molecules/pagination";
import { Dropdown } from "../../../atoms/dropdown";
import { Divider } from "../../../atoms/divider";
import { Table } from "../../../atoms/table";
import { Input } from "../../../atoms/input";
import { Breadcrumb } from "../../../atoms/breadcrumb";
import { usePaginationHook } from "../../../molecules/pagination/usePaginationHook";
import { useConfluenceSpaceListQuery } from "../../../../store/saasTool/saasToolSlice";
import { PAGE_NAME } from "../../../../constants/constants";
import { Icon } from "../../../atoms/icon";

const ConfluenceSpaceList = () => {
  const pageClassName = "p-confluenceSpaceList";

  const ref = React.useRef<HTMLDivElement>(null);
  const filterRef = React.useRef<HTMLDivElement>(null);
  const [isShowFilter, setShowFilter] = React.useState(false);
  const [filter, setFilter] = React.useState({
    type: {
      label: "Select Type",
      value: "",
    },
    status: {
      label: "Select Status",
      value: "",
    },
  });

  const [filterQuery, setFilterQuery] = React.useState({
    type: "",
    status: "",
  });


  const {
    search,
    query,
    pageChangeHandler,
    sortHandler,
    limitChangeHandler,
    searchHandler,
  } = useTableHooks();

  const { data, isLoading, isFetching } = useConfluenceSpaceListQuery({
    search: query.search,
    limit: query.limit,
    current_page: query.current,
    sort_by: pascalToSnakeCase(query.sortBy),
    sort_direction: query.sortDirection,
    tool_name: "Confluence",
    type: filterQuery.type,
    status: filterQuery.status,
  });


  const tableHeader = [
    {
      title: "Serial",
      dataIndex: "id",
      isSortable: true,
    },
    {
      title: "Name",
      dataIndex: "name",
      isSortable: true,
    },
    {
      title: "Type",
      dataIndex: "visibility",
      isSortable: true,
    },
    {
      title: "Status",
      dataIndex: "description",
      isSortable: true,
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      isSortable: true,
    },
  ];

  const toolSortHandler = (sortBy: string) => {
    if (sortBy === "status") {
      sortBy = "description";
    } else if (sortBy === "type") {
      sortBy = "visibility";
    }

    sortHandler(sortBy);
  };

  const {
    totalData,
    handleFirstPage,
    handlePrevPage,
    handleLastPage,
    handleNextPage,
    renderPageNumbers,
  } = usePaginationHook({
    totalNumberOfData: data?.result?.total,
    perPage: query.limit,
    setCurrentPage: pageChangeHandler,
    currentPage: query.current,
    className: "m-pagination",
  });

  const resetFilter = () => {
    setFilter({
      type: {
        label: "Select Type",
        value: "",
      },
      status: {
        label: "Select Status",
        value: "",
      },
    });

    handleFirstPage();
  };

  const filterHandler = () => {
    setFilterQuery({
      ...filterQuery,
      type: filter.type.value,
      status: filter.status.value,
    });
  };

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        filterRef.current &&
        !filterRef.current.contains(event.target as Node)
      ) {
        setShowFilter(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);


  return (
    <div className={pageClassName}>
      <Breadcrumb />
      <p className={`${pageClassName}__title`}>{PAGE_NAME.CONFLUENCE_SPACE_LIST}</p>
      <div className={`${pageClassName}__container`}>
        <div className={`${pageClassName}__header`}>
          <div className={`${pageClassName}__search`}>
            <Input
              placeholder="Search"
              onChange={(e) => {
                searchHandler(e);
                handleFirstPage();
              }}
              value={search}
            />
          </div>

          <div className={`${pageClassName}__filterBy`}>
            {(filterQuery.type ||
              filterQuery.status) && (
                <div className={`${pageClassName}__filterBy__item`}>
                  <p>
                    Type:{" "}
                    <span>
                      {filterQuery.type === "" ? "All" : filterQuery.type}
                    </span>
                    {filterQuery.status && (
                      <span>
                        , Status:{" "}
                        {filterQuery.status === "" ? "All" : filterQuery.status}
                      </span>
                    )}
                  </p>
                </div>
              )}
          </div>

          <div className={`${pageClassName}__filter`} ref={filterRef}>
            <button
              onClick={() => setShowFilter(!isShowFilter)}
              className={`${pageClassName}__filter__button`}
              title="Filter"
            >
              <Icon icon="filter" size="primary" />
            </button>

            {isShowFilter && (
              <div className={`${pageClassName}__filter__container`}>
                <div className={`${pageClassName}__filter__item`} ref={ref}>
                  <label>Type</label>
                  <Dropdown
                    options={[
                      { label: "All", value: "" },
                      { label: "Global", value: "Global" },
                      { label: "Personal", value: "Personal" },
                    ]}
                    selected={filter.type}
                    onClickChange={(e) =>
                      setFilter({
                        ...filter,
                        type: e as { label: string; value: string },
                      })
                    }
                    isBordered
                    isIcon
                  />
                </div>
                <div className={`${pageClassName}__filter__item`} ref={ref}>
                  <label>Status</label>
                  <Dropdown
                    options={[
                      { label: "All", value: "" },
                      { label: "Current", value: "Current" },
                      { label: "Archived", value: "Archived" },
                    ]}
                    selected={filter.status}
                    onClickChange={(e) =>
                      setFilter({
                        ...filter,
                        status: e as { label: string; value: string },
                      })
                    }
                    isBordered
                    isIcon
                  />
                </div>

                <Divider />
                <div className={`${pageClassName}__filter__btn`}>
                  <button onClick={resetFilter}>Reset</button>
                  <button
                    onClick={() => {
                      setShowFilter(false);
                      filterHandler();
                      handleFirstPage();
                    }}
                  >
                    Apply
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className={`${pageClassName}__table`}>
          <Table
            header={tableHeader}
            data={data?.result?.data ?? []}
            loading={isLoading || isFetching}
            firstLoad={isLoading}
            sortHandler={(sortBy: string) => toolSortHandler(sortBy)}
            minWidth="1440"
            minHeight="330"
            startIndex={data?.result?.from}
            sortBy={query.sortBy}
          />
        </div>
        <Divider />

        <div className={`${pageClassName}__pagination`}>
          <div className={`${pageClassName}__pagination__limit`}>
            <p>Display</p>
            <div className={`${pageClassName}__pagination__dropdown`}>
              <Dropdown
                options={[
                  { label: "20", value: 20 },
                  { label: "50", value: 50 },
                  { label: "100", value: 100 },
                ]}
                selected={{ label: query.limit, value: query.limit }}
                onClickChange={(e) => {
                  limitChangeHandler(e.value as number);
                  handleFirstPage();
                }}
                isBordered
                isIcon

              />
            </div>
          </div>
          <div className={`${pageClassName}__pagination__number`}>
            {data?.result?.total ? (
              <p>
                Show {data?.result?.from} -{" "}
                {data?.result?.from + query.limit - 1 < data?.result?.total
                  ? data?.result?.from + query.limit - 1
                  : data?.result?.total}{" "}
                of {data?.result?.total}
              </p>
            ) : (
              <p>Show 00 - 00 of 00</p>
            )}
          </div>
          <div className={`${pageClassName}__pagination__nav`}>
            {data?.result?.total > query.limit && (
              <Pagination
                totalData={totalData}
                page={query.current}
                handleFirstPage={handleFirstPage}
                handlePrevPage={handlePrevPage}
                handleLastPage={handleLastPage}
                handleNextPage={handleNextPage}
                renderPageNumbers={renderPageNumbers}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfluenceSpaceList;
